import { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { DialogUTMWrapper } from 'components/DialogUTM';
import HeadsUpDisplay from 'components/HeadsUpDisplay';
import { IntroProvider } from 'components/introjs';
import StatusSheet from 'components/legacy/StatusSheet';
import AccountRouter from 'components/routers/AccountRouter';
import CartRouter from 'components/routers/CartRouter';
import ChatRouter from 'components/routers/ChatRouter';
import DomainsRouter from 'components/routers/DomainsRouter';
import FeedbackRouter from 'components/routers/FeedbackRouter';
import HomeRouter from 'components/routers/HomeRouter';
import LoadBalancerRouter from 'components/routers/LoadBalancerRouter';
import NetworkRouter from 'components/routers/NetworkRouter';
import ProjectsRouter from 'components/routers/ProjectsRouter';
import SecurityRouter from 'components/routers/SecurityRouter';
import ServersRouter from 'components/routers/ServersRouter';
import ServicesRouter from 'components/routers/ServicesRouter';
import ShopRouter from 'components/routers/ShopRouter';
import SupportRouter from 'components/routers/SupportRouter';
import UserActiveIntent from 'modules/customerIntent/userActive';
import AuthedReduxConnector from 'modules/oidc/AuthedReduxConnector';
import ClubSauceBasicAuthConnector from 'modules/oidc/ClubSauceBasicAuthConnector';
import ClubSauceConnector from 'modules/oidc/ClubSauceConnector';
import ClubSauceUnauthorizedConnector from 'modules/oidc/ClubSauceUnauthorizedConnector';
import GtmConnector from 'modules/oidc/GtmConnector';
import { useOidcSilent } from 'modules/oidc/silentProvider';
import useAuthenticated, {
	useAccountLogin,
	useBasketAdmin,
	useKeroOnly,
	useSetupUser,
	useSuspended,
} from 'modules/oidc/useAuthenticated';
import { PagePreloadProvider } from 'modules/pagePreload';
import useAccountDetails from 'modules/queries/account/useDetails';
import useOrderList from 'modules/queries/order/useList';
import OidcCallback from 'pages/oidc/Callback';
import accountStatuses from 'utility/constants/accountStatuses';
import { roles } from 'utility/constants/roles';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';
import LoginRedirect from './LoginRedirect';
import PageShell from './PageShell';
import SuspenseFallback from './SuspenseFallback';

const Cart = lazy(() => import('pages/shop/Cart'));
const Logout = lazy(() => import('components/Logout'));
const NotFound = lazy(() => import('pages/NotFound'));
const InvoicePDFDisplay = lazy(
	() => import('pages/billing/invoice/InvoicePDFDisplay'),
);
const VNCConsole = lazy(
	() => import('pages/servers/beyond-hosting/VNCConsole'),
);
const AccessDenied = lazy(() => import('pages/AccessDenied'));
const SupportCase = lazy(() => import('pages/support/ticket'));
const OrderConfirmation = lazy(
	() => import('pages/shop/OrderConfirmation/OrderConfirmation'),
);
const RestrictionNoAccountLogin = lazy(
	() => import('components/Restrictions/RestrictionNoAccountLogin'),
);
const RestrictionAccountTerminated = lazy(
	() => import('components/Restrictions/RestrictionAccountTerminated'),
);
const RestrictionVerificationRequired = lazy(
	() => import('components/Restrictions/RestrictionVerificationRequired'),
);
const InvoiceDetails = lazy(() => import('pages/account/InvoiceDetails'));
const MigrationCenter = lazy(() => import('pages/migration/MigrationCenter'));
const BackupsAndRecovery = lazy(
	() => import('pages/storage/BackupsAndRecovery'),
);
const NothingHereYetKeroOnly = lazy(
	() => import('components/NothingHereYet/NothingHereYetKeroOnly'),
);
const NothingHereYetSetupUser = lazy(
	() => import('components/NothingHereYet/NothingHereYetSetupUser'),
);

const AlwaysRoutesRaw = () => [
	<Route key="shopRoute" path="/shop">
		<ShopRouter path="/shop" />
	</Route>,
	<Route key="cartBasketUuid" exact path="/cart/:basketUuid?">
		<Cart />
	</Route>,
	// legacy support for "saved baskets"
	<Route key="cartSavedBasketUuid" exact path="/cart/saved/:basketUuid">
		<Cart />
	</Route>,
];

const AuthenticatedRoutes = () => (
	<PagePreloadProvider fallback={<SuspenseFallback />}>
		<Switch>
			<Route path="/logout">
				<Logout />
			</Route>
			<Route exact path="/shop/complete" component={OrderConfirmation} />
			<Route path="/account/create">
				<Redirect to="/account" />
			</Route>
			<Route path="/" exact>
				<HomeRouter />
			</Route>
			<Route path="/projects">
				<ProjectsRouter />
			</Route>
			<Route path="/servers">
				<ServersRouter />
			</Route>
			<Route path="/domain">
				<DomainsRouter />
			</Route>
			<Route path="/network">
				<NetworkRouter />
			</Route>
			<Route path="/services/load-balancers">
				<LoadBalancerRouter />
			</Route>
			<Route path="/services">
				<ServicesRouter />
			</Route>
			<Route path="/security">
				<SecurityRouter />
			</Route>
			<Route path="/shop">
				<ShopRouter />
			</Route>
			<Redirect path="/billing" to="/account/billing" />
			<RoleRestrictedRoute
				path="/account/billing/invoice/:invoiceId"
				component={InvoiceDetails}
				whitelistedRoles={[
					roles.ACCOUNT_OWNER,
					roles.SECONDARY_OWNER,
					roles.SUSPENDED_OWNER,
					roles.PURCHASER,
				]}
			/>
			<Route path="/account">
				<AccountRouter />
			</Route>
			<Route path="/cart-login">
				<Redirect to="/cart" />
			</Route>
			<Route path="/cart">
				<CartRouter />
			</Route>
			<Route path="/chat">
				<ChatRouter />
			</Route>
			<Route path="/oidc/callback">
				<OidcCallback />
			</Route>
			<RoleRestrictedRoute
				// legacy route
				exact
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
				path="/support/mytickets/:ticket"
				component={({ match }) => (
					<Redirect to={`/support/mycases/${match.params.ticket}`} />
				)}
			/>
			<RoleRestrictedRoute
				exact
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
				path="/support/mycases/:ticket"
				component={SupportCase}
			/>
			<Route path="/support">
				<SupportRouter />
			</Route>
			<Route path="/feedback">
				<FeedbackRouter />
			</Route>
			<RoleRestrictedRoute
				component={BackupsAndRecovery}
				path="/storage/backups-and-recovery"
				whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
			/>
			<Route path="/access-denied">
				<AccessDenied />
			</Route>
			<RoleRestrictedRoute
				exact
				path="/migration-center"
				component={MigrationCenter}
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
			/>
			<Route>
				<NotFound />
			</Route>
		</Switch>
		<DialogUTMWrapper />
	</PagePreloadProvider>
);

const UnAuthenticatedRoutes = () => (
	<Switch>
		{/* the post purchase page and the checkout page are the shop pages that are protected */}
		<Route exact path="/shop/complete">
			<LoginRedirect />
		</Route>
		<Route exact path="/shop/checkout">
			<LoginRedirect />
		</Route>
		{AlwaysRoutesRaw()}
		<Route>
			<LoginRedirect />
		</Route>
	</Switch>
);

const KeroOnlyRoutes = () => (
	<Switch>
		{AlwaysRoutesRaw()}
		<Route path="/logout">
			<Logout />
		</Route>
		<Route path="/oidc/callback">
			<OidcCallback />
		</Route>
		<Route>
			<NothingHereYetKeroOnly />
		</Route>
	</Switch>
);

const SetupUserRoutes = () => (
	<Switch>
		{AlwaysRoutesRaw()}
		<Route path="/logout">
			<Logout />
		</Route>
		<Route path="/oidc/callback">
			<OidcCallback />
		</Route>
		<Route>
			<NothingHereYetSetupUser />
		</Route>
	</Switch>
);

const NewAccountWithPurchaseRoutes = () => (
	<Switch>
		<Route path="/shop/complete">
			<OrderConfirmation />
		</Route>

		<Redirect to="/shop/complete" />
	</Switch>
);

const ExternalRoutes = () => (
	<Switch>
		<Route
			path="/external/invoicepdf/:id"
			render={(props) => <InvoicePDFDisplay {...props} />}
		/>
		<Route
			path="/external/vnc-console/:uniqId"
			render={(props) => <VNCConsole {...props} />}
		/>
		<Route>
			<NotFound />
		</Route>
	</Switch>
);

const AuthenticatedContent = ({
	isKeroOnly,
	isSetupUser,
	isBasketAdmin,
	hasAccountLogin,
	isAccountTerminated,
	verificationRequired,
	isNewAccountWithPurchase,
}) => {
	if (!hasAccountLogin && !isBasketAdmin && !isKeroOnly)
		return <RestrictionNoAccountLogin sx={{ flexGrow: 1 }} />;
	if (isAccountTerminated)
		return <RestrictionAccountTerminated sx={{ flexGrow: 1 }} />;
	if (verificationRequired)
		return <RestrictionVerificationRequired sx={{ flexGrow: 1 }} />;
	if (isNewAccountWithPurchase) return <NewAccountWithPurchaseRoutes />;
	if (isSetupUser) return <SetupUserRoutes />;
	if (isKeroOnly) return <KeroOnlyRoutes />;
	return <AuthenticatedRoutes />;
};

const AuthedHandler = () => {
	const isKeroOnly = useKeroOnly();
	const isSetupUser = useSetupUser();
	const isBasketAdmin = useBasketAdmin();
	const hasAccountLogin = useAccountLogin();
	const isSuspended = useSuspended();
	const { data: accountDetails } = useAccountDetails({});
	const { data: orderData } = useOrderList({
		page_size: 999,
		alsowith: ['veriffURL', 'veriffSessionStatus'],
	});
	const veriffStatus = orderData?.items.find(
		(item) => item.order_id,
	)?.veriffSessionStatus;
	const isNewAccount =
		accountDetails?.account_status === accountStatuses.PENDING_NEW;
	const isAccountTerminated =
		accountDetails?.account_status === accountStatuses.TERMINATED;
	const isOrderRequireCustomerAction = orderData?.items.some(
		(order) => order.status === 'customer_action_required',
	);
	const isAccountVerificationComplete = orderData?.items.some(
		(order) =>
			order.veriffSessionStatus === 'submitted' ||
			order.veriffSessionStatus === 'approved' ||
			order.veriffSessionStatus === 'declined',
	);
	const isCurrentOrderRequireVerification =
		veriffStatus === 'created' ||
		veriffStatus === 'resubmission_requested' ||
		veriffStatus === 'abandoned' ||
		veriffStatus === 'expired';
	const verificationRequired =
		isNewAccount &&
		isOrderRequireCustomerAction &&
		!isAccountVerificationComplete &&
		isCurrentOrderRequireVerification;
	const isNewAccountWithPurchase = isNewAccount && orderData?.items.length > 0;

	return (
		<PageShell
			roles={{
				loggedIn: true,
				keroOnly: isKeroOnly,
				setupUser: isSetupUser,
				basketAdmin: isBasketAdmin,
				accountLogin: hasAccountLogin,
				suspended: isSuspended,
				terminated: isAccountTerminated,
				verificationRequired,
			}}
		>
			<AuthenticatedContent
				isKeroOnly={isKeroOnly}
				isSetupUser={isSetupUser}
				isBasketAdmin={isBasketAdmin}
				hasAccountLogin={hasAccountLogin}
				isAccountTerminated={isAccountTerminated}
				verificationRequired={verificationRequired}
				isNewAccountWithPurchase={isNewAccountWithPurchase}
			/>
		</PageShell>
	);
};

const RouteHandler = () => {
	const [isInitialSilentSigninLoading] = useOidcSilent();
	const isLoggedIn = useAuthenticated();

	if (isInitialSilentSigninLoading) {
		return (
			<Suspense fallback={<SuspenseFallback />}>
				<PageShell>
					<SuspenseFallback />
				</PageShell>
			</Suspense>
		);
	}

	if (isLoggedIn) {
		return (
			<Suspense fallback={<SuspenseFallback />}>
				<IntroProvider>
					<Switch>
						<Route path="/external">
							<ExternalRoutes />
						</Route>
						<Route>
							<AuthedHandler />
						</Route>
					</Switch>

					<ClubSauceConnector />
					<ClubSauceUnauthorizedConnector />
					<GtmConnector />
					<AuthedReduxConnector />
					<StatusSheet />
					<UserActiveIntent />
					<HeadsUpDisplay />
				</IntroProvider>
			</Suspense>
		);
	}

	return (
		<Suspense fallback={<SuspenseFallback />}>
			<PageShell>
				<ClubSauceBasicAuthConnector />
				<UnAuthenticatedRoutes />
				<HeadsUpDisplay />
			</PageShell>
		</Suspense>
	);
};

export default RouteHandler;
